import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ClockIcon,
  BookOpenIcon,
  AcademicCapIcon,
  ChartBarIcon,
  FireIcon,
  CalendarIcon,
  ArrowLeftIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

const StudyLog = () => {
  const [logs, setLogs] = useState([]);
  const [groupedLogs, setGroupedLogs] = useState({});
  const [stats, setStats] = useState(null);
  const [subjectStats, setSubjectStats] = useState({});
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Filters
  const [dateFilter, setDateFilter] = useState('all'); // week, month, 3months, all
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const subjects = ['Matematik', 'Fizik', 'Kimya', 'Biyoloji'];
  const subjectColors = {
    'Matematik': 'bg-blue-500',
    'Fizik': 'bg-purple-500',
    'Kimya': 'bg-green-500',
    'Biyoloji': 'bg-pink-500'
  };

  const activityIcons = {
    'video': '🎬',
    'konu': '📖',
    'soru': '❓',
    'tekrar': '🔄'
  };

  const getDateRange = useCallback(() => {
    const today = new Date();
    let startDate = null;

    if (dateFilter === 'week') {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      startDate = weekAgo.toISOString().split('T')[0];
    } else if (dateFilter === 'month') {
      const monthAgo = new Date(today);
      monthAgo.setDate(monthAgo.getDate() - 30);
      startDate = monthAgo.toISOString().split('T')[0];
    } else if (dateFilter === '3months') {
      const threeMonthsAgo = new Date(today);
      threeMonthsAgo.setDate(threeMonthsAgo.getDate() - 90);
      startDate = threeMonthsAgo.toISOString().split('T')[0];
    }

    return { startDate, endDate: today.toISOString().split('T')[0] };
  }, [dateFilter]);

  const fetchLogs = useCallback(async (page = 1, append = false) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const token = localStorage.getItem('token');
      const { startDate, endDate } = getDateRange();

      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 30);
      if (subjectFilter !== 'all') params.append('subject', subjectFilter);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await axios.get(
        `${API_URL}/ayt-journey/all-logs?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const data = response.data.data;

        if (append) {
          setLogs(prev => [...prev, ...data.logs]);
          // Merge grouped logs
          setGroupedLogs(prev => {
            const merged = { ...prev };
            Object.entries(data.groupedLogs).forEach(([date, logs]) => {
              if (merged[date]) {
                merged[date] = [...merged[date], ...logs];
              } else {
                merged[date] = logs;
              }
            });
            return merged;
          });
        } else {
          setLogs(data.logs);
          setGroupedLogs(data.groupedLogs);
        }

        setStats(data.stats);
        setSubjectStats(data.subjectStats);
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error('Loglar yuklenirken hata:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [API_URL, getDateRange, subjectFilter]);

  useEffect(() => {
    fetchLogs(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter, subjectFilter]);

  const loadMore = () => {
    if (pagination?.hasMore && !loadingMore) {
      fetchLogs(pagination.page + 1, true);
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '0dk';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) return `${hours}sa ${mins}dk`;
    if (hours > 0) return `${hours}sa`;
    return `${mins}dk`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString + 'T12:00:00');
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const dateOnly = new Date(date);
    dateOnly.setHours(12, 0, 0, 0);

    if (dateOnly.getTime() === today.getTime()) return 'Bugun';
    if (dateOnly.getTime() === yesterday.getTime()) return 'Dun';

    const options = { day: 'numeric', month: 'long' };
    const formatted = date.toLocaleDateString('tr-TR', options);

    // Check if same year
    if (date.getFullYear() !== today.getFullYear()) {
      return `${formatted} ${date.getFullYear()}`;
    }

    return formatted;
  };

  const getDateLabel = (dateString) => {
    const date = new Date(dateString + 'T12:00:00');
    const today = new Date();
    const diffDays = Math.floor((today - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Bugun';
    if (diffDays === 1) return 'Dun';
    if (diffDays < 7) return 'Bu Hafta';
    if (diffDays < 30) return 'Bu Ay';
    return 'Onceki';
  };

  // Group dates by label
  const groupedByLabel = {};
  Object.keys(groupedLogs).sort((a, b) => b.localeCompare(a)).forEach(date => {
    const label = getDateLabel(date);
    if (!groupedByLabel[label]) {
      groupedByLabel[label] = [];
    }
    groupedByLabel[label].push({ date, logs: groupedLogs[date] });
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Calisma defteri yukleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Calisma Defterim</h1>
                <p className="text-xs sm:text-sm text-gray-500">
                  Tum AYT calisma kayitlarin
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition ${
                showFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              <FunnelIcon className="h-5 w-5" />
              Filtrele
            </button>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex flex-wrap gap-4">
                {/* Date Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tarih</label>
                  <div className="flex gap-2">
                    {[
                      { value: 'week', label: 'Bu Hafta' },
                      { value: 'month', label: 'Bu Ay' },
                      { value: '3months', label: '3 Ay' },
                      { value: 'all', label: 'Tumu' }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setDateFilter(opt.value)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                          dateFilter === opt.value
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Subject Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ders</label>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => setSubjectFilter('all')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                        subjectFilter === 'all'
                          ? 'bg-gray-800 text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Tumu
                    </button>
                    {subjects.map(subject => (
                      <button
                        key={subject}
                        onClick={() => setSubjectFilter(subject)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                          subjectFilter === subject
                            ? `${subjectColors[subject]} text-white`
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {subject}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Stats Cards */}
          <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-lg bg-blue-50">
                  <ClockIcon className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Toplam Sure</p>
                  <p className="text-lg font-bold text-gray-900">
                    {stats?.totalHours || 0} saat
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-lg bg-green-50">
                  <BookOpenIcon className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Cozulen Soru</p>
                  <p className="text-lg font-bold text-gray-900">
                    {stats?.totalQuestionsAttempted || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-lg bg-purple-50">
                  <AcademicCapIcon className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Basari Orani</p>
                  <p className="text-lg font-bold text-gray-900">
                    %{stats?.successRate || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-lg bg-orange-50">
                  <FireIcon className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Seri</p>
                  <p className="text-lg font-bold text-gray-900">
                    {stats?.streak || 0} gun
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-lg bg-pink-50">
                  <ChartBarIcon className="h-5 w-5 text-pink-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Kayit Sayisi</p>
                  <p className="text-lg font-bold text-gray-900">
                    {stats?.totalLogs || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {Object.keys(groupedByLabel).length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center border border-gray-200">
                <CalendarIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Henuz kayit yok</h3>
                <p className="text-gray-500 mb-4">
                  AYT konularindan calisma kaydi eklediginizde burada gorunecek
                </p>
                <Link
                  to="/ayt-journey"
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  AYT Yolculuguna Git
                </Link>
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(groupedByLabel).map(([label, dateGroups]) => (
                  <div key={label}>
                    <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                      {label}
                    </h2>
                    <div className="space-y-4">
                      {dateGroups.map(({ date, logs: dateLogs }) => (
                        <div key={date} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <CalendarIcon className="h-4 w-4 text-gray-500" />
                                <h3 className="text-sm font-semibold text-gray-900">
                                  {formatDate(date)}
                                </h3>
                              </div>
                              <span className="text-xs text-gray-500">
                                {dateLogs.length} kayit | {formatDuration(dateLogs.reduce((sum, l) => sum + (l.durationMinutes || 0), 0))}
                              </span>
                            </div>
                          </div>

                          <div className="divide-y divide-gray-100">
                            {dateLogs.map((log, idx) => (
                              <div
                                key={log.id || idx}
                                onClick={() => navigate(`/ayt-journey/topic/${log.topicProgressId}`)}
                                className="p-4 hover:bg-gray-50 transition cursor-pointer"
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className={`inline-block w-2 h-2 rounded-full ${subjectColors[log.subject]}`}></span>
                                      <h4 className="font-medium text-gray-900 truncate">
                                        {log.topicName}
                                      </h4>
                                      <span className={`px-2 py-0.5 text-xs rounded-full ${subjectColors[log.subject]} text-white`}>
                                        {log.subject}
                                      </span>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                                      <span className="flex items-center gap-1">
                                        <ClockIcon className="h-4 w-4" />
                                        {formatDuration(log.durationMinutes)}
                                      </span>

                                      {log.activities && log.activities.length > 0 && (
                                        <span className="flex items-center gap-1">
                                          {log.activities.map(act => activityIcons[act] || act).join(' ')}
                                        </span>
                                      )}

                                      {log.progressBefore !== undefined && log.progressAfter !== undefined && log.progressBefore !== log.progressAfter && (
                                        <span className="text-green-600 font-medium">
                                          %{log.progressBefore} → %{log.progressAfter}
                                        </span>
                                      )}

                                      {log.questionsAttempted > 0 && (
                                        <span className="flex items-center gap-1">
                                          <span className="text-green-600">{log.questionsCorrect}</span>
                                          <span className="text-gray-400">/</span>
                                          <span>{log.questionsAttempted}</span>
                                          <span className="text-gray-400">soru</span>
                                        </span>
                                      )}
                                    </div>

                                    {log.note && (
                                      <p className="mt-2 text-sm text-gray-500 bg-gray-50 p-2 rounded-lg truncate">
                                        {log.note}
                                      </p>
                                    )}
                                  </div>

                                  <span className="text-gray-400 text-sm ml-4">→</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Load More Button */}
                {pagination?.hasMore && (
                  <div className="text-center py-4">
                    <button
                      onClick={loadMore}
                      disabled={loadingMore}
                      className="px-6 py-3 bg-white border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
                    >
                      {loadingMore ? (
                        <span className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                          Yukleniyor...
                        </span>
                      ) : (
                        `Daha Fazla Goster (${pagination.totalLogs - logs.length} kayit)`
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar - Subject Stats */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200 sticky top-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Ders Bazli Ozet</h3>

              {Object.values(subjectStats).every(s => s.logCount === 0) ? (
                <p className="text-sm text-gray-500 text-center py-4">Henuz veri yok</p>
              ) : (
                <div className="space-y-4">
                  {subjects.map(subject => {
                    const data = subjectStats[subject] || { totalMinutes: 0, questionsAttempted: 0, logCount: 0 };
                    if (data.logCount === 0) return null;

                    const totalMinutes = Object.values(subjectStats).reduce((sum, s) => sum + s.totalMinutes, 0);
                    const percentage = totalMinutes > 0 ? Math.round((data.totalMinutes / totalMinutes) * 100) : 0;

                    return (
                      <div key={subject} className="border-b border-gray-100 pb-3 last:border-0">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`w-3 h-3 rounded-full ${subjectColors[subject]}`}></span>
                            <h4 className="font-medium text-gray-900 text-sm">{subject}</h4>
                          </div>
                          <span className="text-xs text-gray-500">{data.logCount} kayit</span>
                        </div>

                        <div className="space-y-1 text-xs text-gray-600">
                          <div className="flex justify-between">
                            <span>Sure:</span>
                            <span className="font-medium">{formatDuration(data.totalMinutes)}</span>
                          </div>
                          {data.questionsAttempted > 0 && (
                            <div className="flex justify-between">
                              <span>Soru:</span>
                              <span className="font-medium">
                                {data.questionsCorrect}/{data.questionsAttempted}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className={`${subjectColors[subject]} h-1.5 rounded-full transition-all duration-300`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Quick Link to AYT Journey */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <Link
                  to="/ayt-journey"
                  className="block w-full text-center px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:shadow-md transition text-sm"
                >
                  AYT Yolculuguna Git
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudyLog;
